import React, { useState } from 'react';
import {
  View,
  Text,
  TouchableOpacity,
  StyleSheet,
  Alert,
  Platform,
  ScrollView,
  ActivityIndicator,
  Image,
  ImageBackground,
  KeyboardAvoidingView,
  Modal,
  TextInput,
  Linking,
} from 'react-native';
import LinearGradient from 'react-native-linear-gradient';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { Colors, Typography, Spacing, Radius, Shadow } from '../theme';
import type { useAuth } from '../hooks/useAuth';
import { isValidEmailFormat } from '../hooks/useAuth';
import { requestNotificationPermission } from '../utils/permissions';
import { useI18n } from '../i18n';
import { PendingVibeClaimBanner, VibeCodeEntryLink } from '../features/vibeClaim/VibeCodeEntry';
import TonyteLoadingScreen from '../components/TonyteLoadingScreen';
import LegalLink from '../components/LegalLink';

const TERMS_URL = 'https://tonyte.app/terms';
const PRIVACY_URL = 'https://tonyte.app/privacy';

const loginBackground = require('../assets/BG.jpg');
const loginLogo = require('../assets/login_logo.png');
const googleIcon = { uri: 'https://www.google.com/favicon.ico' };

// Apple Sign-In on web (src/hooks/appleSignIn.web.ts, a real
// signInWithPopup(OAuthProvider('apple.com')) implementation) needs external
// Apple Developer + Firebase Console configuration that isn't done yet —
// until then, tapping the button always fails with "This sign-in method is
// not enabled yet.", a broken-looking option for every web visitor. Hidden
// on web only via this single flag rather than removing any code; flip back
// to true the moment that external configuration is complete — nothing else
// needs to change. Native iOS is unaffected either way (see the render
// condition below, which only ever gates the web case on this flag).
const WEB_APPLE_SIGN_IN_ENABLED = false;

type FeatureIconName = 'people' | 'calendar' | 'message';

const EmailIcon = () => (
  <View style={styles.emailIconFrame}>
    <View style={[styles.emailIconFold, styles.emailIconFoldLeft]} />
    <View style={[styles.emailIconFold, styles.emailIconFoldRight]} />
  </View>
);

const AppleMark = () => (
  <Text style={styles.appleMark} allowFontScaling={false}>
    
  </Text>
);

const FeatureIcon = ({ name }: { name: FeatureIconName }) => {
  if (name === 'calendar') {
    return (
      <View style={styles.calendarIcon}>
        <View style={styles.calendarTop} />
        <View style={[styles.calendarRing, styles.calendarRingLeft]} />
        <View style={[styles.calendarRing, styles.calendarRingRight]} />
        <View style={styles.calendarDotRow}>
          <View style={styles.calendarDot} />
          <View style={styles.calendarDot} />
          <View style={styles.calendarDot} />
        </View>
      </View>
    );
  }

  if (name === 'message') {
    return (
      <View style={styles.messageIcon}>
        <View style={styles.messageLine} />
        <View style={[styles.messageLine, styles.messageLineShort]} />
        <View style={styles.messageTail} />
      </View>
    );
  }

  return (
    <View style={styles.peopleIcon}>
      <View style={[styles.personHead, styles.personHeadBack]} />
      <View style={[styles.personHead, styles.personHeadFront]} />
      <View style={[styles.personBody, styles.personBodyBack]} />
      <View style={[styles.personBody, styles.personBodyFront]} />
    </View>
  );
};

interface Props {
  onEmailSignIn: ReturnType<typeof useAuth>['signInWithEmail'];
  onPasswordReset: ReturnType<typeof useAuth>['resetPassword'];
  onGoogleSignIn: ReturnType<typeof useAuth>['signInWithGoogle'];
  onAppleSignIn: ReturnType<typeof useAuth>['signInWithApple'];
  // True from the instant a sign-in button is tapped until Firebase auth
  // settles. Rendered as a full-screen overlay INSIDE this component
  // (rather than have the root navigator swap LoginScreen out for
  // TonyteLoadingScreen) so a failed attempt reverts to this exact same
  // component instance — email/password typed in and the sign-in modal's
  // open state survive a failed attempt instead of being reset by a remount.
  isSigningIn?: boolean;
}

const LoginScreen: React.FC<Props> = ({
  onEmailSignIn,
  onPasswordReset,
  onGoogleSignIn,
  onAppleSignIn,
  isSigningIn = false,
}) => {
  const { t } = useI18n();
  const insets = useSafeAreaInsets();
  const [loading, setLoading] = useState<'email' | 'reset' | 'google' | 'apple' | null>(null);
  const [emailModalOpen, setEmailModalOpen] = useState(false);
  const [emailMode, setEmailMode] = useState<'signin' | 'signup'>('signin');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');

  const handleEmailSubmit = async () => {
    const normalizedEmail = email.trim();
    if (!normalizedEmail || !password) {
      Alert.alert(t('login.missingDetails'), t('login.enterEmailPassword'));
      return;
    }
    if (emailMode === 'signup' && !isValidEmailFormat(normalizedEmail)) {
      Alert.alert(t('login.missingDetails'), t('auth.invalidEmail'));
      return;
    }
    if (password.length < 6) {
      Alert.alert(t('login.passwordTooShort'), t('login.passwordMin'));
      return;
    }

    setLoading('email');
    try {
      await onEmailSignIn(normalizedEmail, password, emailMode);
      requestNotificationPermission().catch(error => {
        console.warn('[Tonyte][RN][Permissions] notification request failed', error);
      });
      setEmailModalOpen(false);
      setEmail('');
      setPassword('');
      setEmailMode('signin');
    } catch (err: any) {
      Alert.alert(t('login.emailSignInFailed'), err?.message ?? t('common.somethingWentWrong'));
    } finally {
      setLoading(null);
    }
  };

  const handlePasswordReset = async () => {
    const normalizedEmail = email.trim();
    if (!normalizedEmail) {
      Alert.alert(t('login.emailRequired'), t('login.enterResetEmail'));
      return;
    }

    setLoading('reset');
    try {
      await onPasswordReset(normalizedEmail);
      Alert.alert(t('login.resetEmailSent'), t('login.resetEmailSentMessage'));
    } catch (err: any) {
      Alert.alert(t('login.passwordResetFailed'), err?.message ?? t('common.somethingWentWrong'));
    } finally {
      setLoading(null);
    }
  };

  const handleGoogle = async () => {
    setLoading('google');
    try {
      await onGoogleSignIn();
      requestNotificationPermission().catch(error => {
        console.warn('[Tonyte][RN][Permissions] notification request failed', error);
      });
    } catch (err: any) {
      if (err?.code !== 'SIGN_IN_CANCELLED') {
        Alert.alert(t('login.signInFailed'), err?.message ?? t('common.somethingWentWrong'));
      }
    } finally {
      setLoading(null);
    }
  };

  const handleApple = async () => {
    setLoading('apple');
    try {
      await onAppleSignIn();
      requestNotificationPermission().catch(error => {
        console.warn('[Tonyte][RN][Permissions] notification request failed', error);
      });
    } catch (err: any) {
      if (err?.code !== '1001') {
        // 1001 = cancelled
        Alert.alert(t('login.appleSignInFailed'), err?.message ?? t('common.somethingWentWrong'));
      }
    } finally {
      setLoading(null);
    }
  };

  if (isSigningIn) {
    return <TonyteLoadingScreen message={t('common.findingYourVibe')} />;
  }

  return (
    <ImageBackground
      source={loginBackground}
      style={[styles.container, { paddingBottom: insets.bottom + Spacing.xl }]}
      resizeMode="cover"
      imageStyle={styles.backgroundImage}
    >
      <LinearGradient
        colors={['rgba(11,28,44,0.18)', 'rgba(5,10,18,0.68)', 'rgba(5,10,18,0.98)']}
        locations={[0, 0.56, 1]}
        style={StyleSheet.absoluteFill}
      />

      <Modal
        visible={emailModalOpen}
        animationType="fade"
        transparent
        onRequestClose={() => setEmailModalOpen(false)}
      >
        <KeyboardAvoidingView
          style={styles.modalOverlay}
          behavior={Platform.OS === 'ios' ? 'padding' : undefined}
        >
          <View style={styles.emailModal}>
            <View style={styles.modalHeader}>
              <View>
                <Text style={styles.modalKicker}>Tonyte</Text>
                <Text style={styles.modalTitle}>
                  {emailMode === 'signin' ? t('login.signInWithEmail') : t('login.createYourAccount')}
                </Text>
              </View>
              <TouchableOpacity
                style={styles.modalClose}
                onPress={() => setEmailModalOpen(false)}
                disabled={loading !== null}
              >
                <Text style={styles.modalCloseText}>x</Text>
              </TouchableOpacity>
            </View>

            <View style={styles.modeSwitch}>
              {(['signin', 'signup'] as const).map(mode => (
                <TouchableOpacity
                  key={mode}
                  style={[styles.modeButton, emailMode === mode && styles.modeButtonActive]}
                  onPress={() => setEmailMode(mode)}
                  disabled={loading !== null}
                >
                  <Text style={[
                    styles.modeButtonText,
                    emailMode === mode && styles.modeButtonTextActive,
                  ]}>
                    {mode === 'signin' ? t('login.signIn') : t('login.create')}
                  </Text>
                </TouchableOpacity>
              ))}
            </View>

            <TextInput
              style={styles.emailInput}
              value={email}
              onChangeText={setEmail}
              autoCapitalize="none"
              autoCorrect={false}
              keyboardType="email-address"
              textContentType="emailAddress"
              placeholder={t('login.email')}
              placeholderTextColor={Colors.textMuted}
            />
            <TextInput
              style={styles.emailInput}
              value={password}
              onChangeText={setPassword}
              secureTextEntry
              textContentType={emailMode === 'signin' ? 'password' : 'newPassword'}
              placeholder={t('login.password')}
              placeholderTextColor={Colors.textMuted}
            />

            {emailMode === 'signin' && (
              <TouchableOpacity
                style={styles.forgotPasswordButton}
                onPress={handlePasswordReset}
                disabled={loading !== null}
              >
                <Text style={styles.forgotPasswordText}>{t('login.forgotPassword')}</Text>
              </TouchableOpacity>
            )}

            <TouchableOpacity
              style={styles.modalSubmit}
              onPress={handleEmailSubmit}
              disabled={loading !== null}
              activeOpacity={0.85}
            >
              {loading === 'email' ? (
                <ActivityIndicator size="small" color={Colors.navy} />
              ) : (
                <Text style={styles.modalSubmitText}>
                  {emailMode === 'signin' ? t('login.signIn') : t('login.createAccount')}
                </Text>
              )}
            </TouchableOpacity>
          </View>
        </KeyboardAvoidingView>
      </Modal>

      <ScrollView
        contentContainerStyle={styles.scroll}
        keyboardShouldPersistTaps="handled"
        showsVerticalScrollIndicator={false}
      >
        <View style={[styles.logoArea, { paddingTop: insets.top + Spacing.xxxl }]}>
          <Image source={loginLogo} style={styles.logoImage} resizeMode="contain" />
          <Text style={styles.appName}>
            Tony<Text style={styles.appNameAccent}>te</Text>
          </Text>
        </View>

        <View style={styles.pitchArea}>
          <Text style={styles.pitchTitle}>{t('login.tagline')}</Text>
          <Text style={styles.tagline}>
            {t('login.findYour')} <Text style={styles.pitchAccent}>{t('login.people')}</Text>.
            {' '}
            {t('login.findYour')} <Text style={styles.pitchAccent}>{t('login.plans')}</Text>.
          </Text>
          <View style={styles.featureGrid}>
            {[
              ['people', t('login.featurePeople')],
              ['calendar', t('login.featureEvents')],
              ['message', t('login.featureAlone')],
            ].map(([icon, label]) => (
              <View style={styles.featureCard} key={label}>
                <View style={styles.featureIcon}>
                  <FeatureIcon name={icon as FeatureIconName} />
                </View>
                <Text style={styles.featureLabel}>{label}</Text>
              </View>
            ))}
          </View>
        </View>

        <View style={styles.buttonsArea}>
          {/* Web Vibe game answers waiting to be claimed after sign-in/sign-up. */}
          {Platform.OS !== 'web' && <PendingVibeClaimBanner />}
          <TouchableOpacity
            style={styles.emailBtn}
            onPress={() => setEmailModalOpen(true)}
            disabled={loading !== null}
            activeOpacity={0.85}
          >
            <EmailIcon />
            <Text style={styles.emailBtnText}>{t('login.continueWithEmail')}</Text>
          </TouchableOpacity>

          <View style={styles.providerGrid}>
          {/* Google */}
          <TouchableOpacity
            style={styles.socialBtn}
            onPress={handleGoogle}
            disabled={loading !== null}
            activeOpacity={0.85}
          >
            {loading === 'google' ? (
              <ActivityIndicator size="small" color={Colors.textPrimary} />
            ) : (
              <>
                <Image source={googleIcon} style={styles.brandIcon} />
                <Text style={styles.socialBtnText}>{t('login.continueWithGoogle')}</Text>
              </>
            )}
          </TouchableOpacity>

          {/* Apple — always shown on iOS. On web, gated behind
              WEB_APPLE_SIGN_IN_ENABLED (see top of file) until Apple/Firebase's
              web-side config is complete; see src/hooks/appleSignIn.web.ts. */}
          {(Platform.OS === 'ios' || (Platform.OS === 'web' && WEB_APPLE_SIGN_IN_ENABLED)) && (
            <TouchableOpacity
              style={[styles.socialBtn, styles.appleBtn]}
              onPress={handleApple}
              disabled={loading !== null}
              activeOpacity={0.85}
            >
              {loading === 'apple' ? (
                <ActivityIndicator size="small" color="#000" />
              ) : (
                <>
                  <AppleMark />
                  <Text style={styles.appleBtnText}>{t('login.continueWithApple')}</Text>
              </>
            )}
          </TouchableOpacity>
          )}
          </View>

          <Text style={styles.legalText}>
            {t('login.legalPrefix')}{' '}
            <LegalLink href={TERMS_URL} inline style={styles.legalLink} onPress={() => void Linking.openURL(TERMS_URL)}>
              {t('settings.termsOfService')}
            </LegalLink>
            {' '}{t('login.and')}{' '}
            <LegalLink href={PRIVACY_URL} inline style={styles.legalLink} onPress={() => void Linking.openURL(PRIVACY_URL)}>
              {t('settings.privacyPolicy')}
            </LegalLink>
          </Text>

          <TouchableOpacity
            onPress={() => {
              setEmailMode('signin');
              setEmailModalOpen(true);
            }}
            disabled={loading !== null}
            style={styles.partnerLoginLink}
          >
            <Text style={styles.partnerLoginLinkText}>{t('login.partnerSignInHint')}</Text>
          </TouchableOpacity>
          {Platform.OS !== 'web' && <VibeCodeEntryLink signedIn={false} />}
        </View>
      </ScrollView>
    </ImageBackground>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: Colors.bg,
  },
  backgroundImage: {
    transform: [{ translateY: 18 }],
  },
  scroll: {
    flexGrow: 1,
    paddingHorizontal: Spacing.xl,
    paddingBottom: Spacing.sm,
  },
  modalOverlay: {
    flex: 1,
    justifyContent: 'center',
    paddingHorizontal: Spacing.xl,
    backgroundColor: 'rgba(0,0,0,0.62)',
  },
  emailModal: {
    borderRadius: Radius.xxl,
    borderWidth: 1,
    borderColor: Colors.borderStrong,
    backgroundColor: 'rgba(10,14,23,0.98)',
    padding: Spacing.lg,
    gap: Spacing.md,
    ...Shadow.strong,
  },
  modalHeader: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: Spacing.base,
  },
  modalKicker: {
    color: Colors.sunset,
    fontSize: Typography.xs,
    fontWeight: Typography.bold,
    textTransform: 'uppercase',
  },
  modalTitle: {
    color: Colors.textPrimary,
    fontSize: Typography.lg,
    fontWeight: Typography.bold,
    marginTop: 3,
  },
  modalClose: {
    width: 34,
    height: 34,
    borderRadius: 17,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'rgba(255,255,255,0.08)',
  },
  modalCloseText: {
    color: Colors.textSecondary,
    fontSize: Typography.base,
    fontWeight: Typography.bold,
  },
  modeSwitch: {
    flexDirection: 'row',
    padding: 3,
    borderRadius: Radius.lg,
    backgroundColor: 'rgba(255,255,255,0.07)',
  },
  modeButton: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: Radius.md,
    paddingVertical: Spacing.sm,
  },
  modeButtonActive: {
    backgroundColor: Colors.sunset,
  },
  modeButtonText: {
    color: Colors.textSecondary,
    fontSize: Typography.sm,
    fontWeight: Typography.semibold,
  },
  modeButtonTextActive: {
    color: '#fff',
  },
  emailInput: {
    backgroundColor: Colors.bgInput,
    borderWidth: 1,
    borderColor: Colors.border,
    borderRadius: Radius.lg,
    paddingHorizontal: Spacing.base,
    paddingVertical: Spacing.md,
    color: Colors.textPrimary,
    fontSize: Typography.base,
  },
  forgotPasswordButton: {
    alignSelf: 'flex-end',
    paddingVertical: Spacing.xs,
  },
  forgotPasswordText: {
    color: Colors.turquoise,
    fontSize: Typography.sm,
    fontWeight: Typography.semibold,
  },
  modalSubmit: {
    minHeight: 48,
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: Radius.lg,
    backgroundColor: '#fff',
  },
  modalSubmitText: {
    color: Colors.navy,
    fontSize: Typography.base,
    fontWeight: Typography.bold,
  },
  logoArea: {
    alignItems: 'center',
    marginBottom: Spacing.lg,
  },
  logoImage: {
    width: 104,
    height: 132,
    marginBottom: 2,
    opacity: 0.96,
  },
  appName: {
    color: Colors.textPrimary,
    fontSize: Typography.xxxl,
    fontWeight: Typography.extrabold,
    letterSpacing: 0,
  },
  appNameAccent: {
    color: '#FF7A00',
  },
  pitchArea: {
    alignItems: 'center',
  },
  pitchTitle: {
    color: Colors.textPrimary,
    fontSize: 38,
    lineHeight: 46,
    fontWeight: Typography.extrabold,
    textAlign: 'center',
    letterSpacing: 0,
    paddingBottom: 2,
  },
  pitchAccent: {
    color: '#FF7A00',
  },
  tagline: {
    color: Colors.textSecondary,
    fontSize: Typography.base,
    lineHeight: 23,
    marginTop: Spacing.md,
    fontWeight: Typography.medium,
    textAlign: 'center',
  },
  featureGrid: {
    flexDirection: 'row',
    gap: Spacing.sm,
    marginTop: Spacing.lg,
  },
  featureCard: {
    flex: 1,
    minHeight: 76,
    alignItems: 'center',
    justifyContent: 'center',
    gap: Spacing.xs,
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.06)',
    borderRadius: Radius.xl,
    backgroundColor: 'rgba(0,0,0,0.08)',
    paddingHorizontal: Spacing.xs,
  },
  featureIcon: {
    width: 24,
    height: 24,
    alignItems: 'center',
    justifyContent: 'center',
    shadowColor: '#FF7A00',
    shadowOffset: { width: 0, height: 0 },
    shadowOpacity: 0.38,
    shadowRadius: 10,
  },
  peopleIcon: {
    width: 24,
    height: 22,
  },
  personHead: {
    position: 'absolute',
    borderRadius: 99,
    backgroundColor: '#FF7A00',
    borderWidth: 1,
    borderColor: 'rgba(255,190,126,0.55)',
  },
  personHeadBack: {
    width: 9,
    height: 9,
    left: 3,
    top: 4,
    opacity: 0.72,
  },
  personHeadFront: {
    width: 11,
    height: 11,
    right: 4,
    top: 2,
  },
  personBody: {
    position: 'absolute',
    borderRadius: 99,
    backgroundColor: 'rgba(255,122,0,0.34)',
    borderWidth: 1.4,
    borderColor: '#FF7A00',
  },
  personBodyBack: {
    width: 14,
    height: 8,
    left: 1,
    bottom: 2,
    opacity: 0.66,
  },
  personBodyFront: {
    width: 17,
    height: 9,
    right: 1,
    bottom: 1,
  },
  calendarIcon: {
    width: 22,
    height: 22,
    borderRadius: 5,
    borderWidth: 1.7,
    borderColor: '#FF7A00',
    overflow: 'hidden',
    backgroundColor: 'rgba(255,122,0,0.08)',
  },
  calendarTop: {
    height: 6,
    backgroundColor: 'rgba(255,122,0,0.38)',
    borderBottomWidth: 1,
    borderBottomColor: '#FF7A00',
  },
  calendarRing: {
    position: 'absolute',
    top: -2,
    width: 3,
    height: 7,
    borderRadius: 2,
    backgroundColor: '#FF7A00',
  },
  calendarRingLeft: {
    left: 5,
  },
  calendarRingRight: {
    right: 5,
  },
  calendarDotRow: {
    flexDirection: 'row',
    gap: 3,
    justifyContent: 'center',
    marginTop: 4,
  },
  calendarDot: {
    width: 3,
    height: 3,
    borderRadius: 2,
    backgroundColor: '#FF7A00',
  },
  messageIcon: {
    width: 24,
    height: 18,
    borderRadius: 6,
    borderWidth: 1.8,
    borderColor: '#FF7A00',
    backgroundColor: 'rgba(255,122,0,0.08)',
    paddingHorizontal: 4,
    paddingTop: 5,
  },
  messageLine: {
    height: 2,
    borderRadius: 1,
    backgroundColor: '#FF7A00',
    marginBottom: 3,
  },
  messageLineShort: {
    width: 9,
  },
  messageTail: {
    position: 'absolute',
    right: 3,
    bottom: -4,
    width: 8,
    height: 8,
    borderRightWidth: 1.8,
    borderBottomWidth: 1.8,
    borderColor: '#FF7A00',
    backgroundColor: 'rgba(255,122,0,0.08)',
    transform: [{ rotate: '45deg' }],
  },
  featureLabel: {
    color: 'rgba(255,255,255,0.72)',
    fontSize: Typography.xs,
    fontWeight: Typography.semibold,
    textAlign: 'center',
    lineHeight: 14,
  },
  buttonsArea: {
    gap: Spacing.md,
    marginTop: 'auto',
    paddingTop: Spacing.xxxl,
  },
  emailBtn: {
    flexDirection: 'row',
    backgroundColor: '#fff',
    borderRadius: Radius.xl,
    paddingVertical: Spacing.md + 2,
    alignItems: 'center',
    justifyContent: 'center',
    gap: Spacing.sm,
  },
  emailBtnText: {
    color: Colors.navy,
    fontSize: Typography.base,
    fontWeight: Typography.bold,
  },
  providerGrid: {
    flexDirection: 'column',
    gap: Spacing.md,
  },
  socialBtn: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'rgba(255,255,255,0.08)',
    borderWidth: 1,
    borderColor: Colors.borderStrong,
    borderRadius: Radius.lg,
    paddingVertical: Spacing.md + 2,
    gap: Spacing.sm,
  },
  brandIcon: {
    width: 20,
    height: 20,
  },
  emailIconFrame: {
    width: 22,
    height: 16,
    borderRadius: 4,
    borderWidth: 1.8,
    borderColor: Colors.navy,
    overflow: 'hidden',
  },
  emailIconFold: {
    position: 'absolute',
    top: 4,
    width: 15,
    height: 1.8,
    borderRadius: 1,
    backgroundColor: Colors.navy,
  },
  emailIconFoldLeft: {
    left: -1,
    transform: [{ rotate: '31deg' }],
  },
  emailIconFoldRight: {
    right: -1,
    transform: [{ rotate: '-31deg' }],
  },
  socialBtnText: {
    color: Colors.textPrimary,
    fontSize: Typography.sm,
    fontWeight: Typography.semibold,
  },
  appleBtn: {
    backgroundColor: '#fff',
    borderColor: '#fff',
  },
  appleMark: {
    color: '#000',
    fontSize: 22,
    lineHeight: 22,
    fontWeight: Typography.medium,
  },
  appleBtnText: {
    color: '#000',
    fontSize: Typography.sm,
    fontWeight: Typography.semibold,
  },
  legalText: {
    color: Colors.textMuted,
    fontSize: Typography.xs,
    textAlign: 'center',
    lineHeight: 18,
    marginTop: Spacing.sm,
  },
  legalLink: {
    color: Colors.sunset,
    textDecorationLine: 'underline',
  },
  partnerLoginLink: {
    marginTop: Spacing.md,
    alignSelf: 'center',
  },
  partnerLoginLinkText: {
    color: Colors.textMuted,
    fontSize: Typography.xs,
    textDecorationLine: 'underline',
  },
});

export default LoginScreen;
